import { useRef, memo, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, Vector3 } from 'three';
import { Html } from '@react-three/drei';
import { usePCAStore } from '../stores/pcaStore';
import type { GuestPoint as GuestPointType } from '../types/pca';

interface GuestPointProps extends GuestPointType {
  isHovered: boolean;
  isSelected: boolean;
  isComparison: boolean; // Whether this is the comparison guest
  hasSelection: boolean; // Whether any point is currently selected
  isInSelectedCluster: boolean; // Whether this point belongs to the selected cluster
  hasClusterSelection: boolean; // Whether any cluster is selected
  isUserPoint?: boolean; // Whether this is the user's point
  cameraPosition?: Vector3; // For proximity-based labels
  onHover: (hovered: boolean) => void;
  onClick: () => void;
}

// Distance threshold for showing proximity labels
const PROXIMITY_LABEL_DISTANCE = 4.5;

// Minimum visibility threshold - below this, point is hidden
const MIN_VISIBILITY_THRESHOLD = 0.05;

export const GuestPoint = memo(function GuestPoint({
  name,
  position,
  color,
  size,
  visibility = 1,
  isHovered,
  isSelected,
  isComparison,
  hasSelection,
  isInSelectedCluster,
  hasClusterSelection,
  isUserPoint = false,
  cameraPosition,
  onHover,
  onClick
}: GuestPointProps) {
  // ALL HOOKS MUST BE AT THE TOP - before any conditional returns
  const meshRef = useRef<Mesh>(null);
  const ringRef = useRef<Mesh>(null);
  const hoverRingRef = useRef<Mesh>(null);
  const { isQuizActive } = usePCAStore();
  
  // Check if this point should be visible
  const isVisible = visibility >= MIN_VISIBILITY_THRESHOLD;
  
  // Calculate distance from camera for proximity labels
  const isNearCamera = useMemo(() => {
    if (!cameraPosition) return false;
    const pointPos = new Vector3(position[0], position[1], position[2]);
    const distance = cameraPosition.distanceTo(pointPos);
    return distance < PROXIMITY_LABEL_DISTANCE;
  }, [cameraPosition, position]);
  
  // Show label when selected, hovered, or near camera (but not when quiz active or when there's a selection and this isn't it)
  const showLabel = !isQuizActive && (
    isSelected || 
    isHovered || 
    (isNearCamera && !hasSelection && !hasClusterSelection)
  );

  // Calculate target scale based on visibility, hover, selection, and cluster selection
  const { targetScale, targetOpacity, targetEmissiveIntensity } = useMemo(() => {
    // User point: always larger and pulsating
    if (isUserPoint) {
      return {
        targetScale: 2.5,
        targetOpacity: 1.0,
        targetEmissiveIntensity: 1.2, // Strong red glow
        userPulseScale: 1.0 // Will animate in useFrame
      };
    }
    
    // Base scale from interaction state - selected points are larger, comparison is medium
    let baseScale = isSelected ? 2.2 : isComparison ? 2.0 : isHovered ? 1.4 : 1.0;
    
    // Scale up points in selected cluster
    if (hasClusterSelection && isInSelectedCluster && !isSelected && !isComparison && !isHovered) {
      baseScale = 1.5;
    }
    
    // Apply visibility to scale (shrink faded points)
    const visibilityScale = 0.3 + visibility * 0.7; // Scale from 0.3 to 1.0
    const scale = baseScale * visibilityScale;
    
    // Calculate opacity - fade non-selected/comparison points when there's a selection
    let opacity = 1.0;
    if (hasClusterSelection) {
      // Cluster selection takes precedence
      if (isInSelectedCluster) {
        opacity = 1.0;
      } else {
        opacity = 0.15; // Fade points not in cluster
      }
    } else if (hasSelection && !isSelected && !isComparison) {
      // Fade other points when something is selected
      opacity = 0.25;
    } else if (isHovered || isSelected || isComparison) {
      opacity = 1.0;
    } else {
      opacity = 0.8;
    }
    opacity *= visibility;
    
    // Emissive intensity - stronger for selected points, medium for comparison
    let emissiveIntensity = 0.1;
    if (isSelected) {
      emissiveIntensity = 0.7; // Much stronger glow for selected
    } else if (isComparison) {
      emissiveIntensity = 0.7; // Strong glow for comparison (same as selected)
    } else if (hasClusterSelection && isInSelectedCluster) {
      emissiveIntensity = 0.35; // Highlight cluster points
    } else if (isHovered) {
      emissiveIntensity = 0.4;
    }
    emissiveIntensity *= visibility;
    
    return { targetScale: scale, targetOpacity: opacity, targetEmissiveIntensity: emissiveIntensity };
  }, [visibility, isHovered, isSelected, isComparison, hasSelection, isInSelectedCluster, hasClusterSelection, isUserPoint]);

  // Pulsating animation for user point
  const pulseRef = useRef(1.0);
  
  // Animate scale, opacity, and emissive intensity
  useFrame(({ clock }) => {
    if (!isVisible) return; // Skip animation if not visible
    
    // User point: pulsating effect
    if (isUserPoint) {
      pulseRef.current = 1.0 + Math.sin(clock.elapsedTime * 2) * 0.2; // Pulse between 0.8 and 1.2
    }
    
    if (meshRef.current) {
      const finalScale = isUserPoint ? targetScale * pulseRef.current : targetScale;
      meshRef.current.scale.lerp(
        { x: finalScale, y: finalScale, z: finalScale } as any,
        0.15
      );
      
      // Animate material properties
      const material = meshRef.current.material as any;
      if (material) {
        if ('opacity' in material) {
          material.opacity += (targetOpacity - material.opacity) * 0.15;
        }
        if ('emissiveIntensity' in material) {
          const finalEmissive = isUserPoint ? targetEmissiveIntensity * (0.8 + pulseRef.current * 0.2) : targetEmissiveIntensity;
          material.emissiveIntensity += (finalEmissive - material.emissiveIntensity) * 0.15;
        }
      }
    }
  });

  // Pulsing ring for selected/comparison/user point
  useFrame(({ clock }) => {
    if (!isVisible) return; // Skip if not visible
    if (ringRef.current && (isSelected || isComparison || isUserPoint)) {
      if (isUserPoint) {
        // User point: strong pulsating red ring
        const scale = 1.0 + Math.sin(clock.elapsedTime * 2.5) * 0.3;
        ringRef.current.scale.set(scale, scale, scale);
        const material = ringRef.current.material as any;
        if (material && 'opacity' in material) {
          material.opacity = 0.4 + Math.sin(clock.elapsedTime * 2.5) * 0.3;
        }
      } else {
        // Larger pulse range for selected, smaller for comparison
        const pulseRange = isSelected ? 0.4 : 0.2;
        const baseScale = isSelected ? 2.0 : 1.5;
        const scale = baseScale + Math.sin(clock.elapsedTime * 3) * pulseRange;
        ringRef.current.scale.set(scale, scale, scale);
        const material = ringRef.current.material as any;
        if (material && 'opacity' in material) {
          // More visible pulse for selected
          const baseOpacity = isSelected ? 0.5 : 0.3;
          material.opacity = baseOpacity + Math.sin(clock.elapsedTime * 3) * 0.3;
        }
      }
    }
  });

  // Hover ring for gentle highlighting
  useFrame(({ clock }) => {
    if (!isVisible) return; // Skip if not visible
    if (hoverRingRef.current && isHovered && !isSelected && !isComparison) {
      const pulse = Math.sin(clock.elapsedTime * 4) * 0.1 + 0.9;
      hoverRingRef.current.scale.set(pulse, pulse, pulse);
      const material = hoverRingRef.current.material as any;
      if (material && 'opacity' in material) {
        material.opacity = 0.3 + Math.sin(clock.elapsedTime * 4) * 0.1;
      }
    }
  });

  // Don't render if visibility is too low - but AFTER all hooks are called
  if (!isVisible) {
    return null;
  }

  return (
    <group position={position}>
      {/* User point: Always show pulsating red ring - fixed size for consistency */}
      {isUserPoint && (
        <mesh ref={ringRef}>
          <ringGeometry args={[0.35, 0.50, 32]} />
          <meshBasicMaterial
            color="#FF0000"
            transparent
            opacity={0.6}
            side={2} // DoubleSide
          />
        </mesh>
      )}
      
      {/* Pulsing ring for selected/comparison point (non-user) */}
      {/* Ring uses fixed base + small size factor to prevent enormous rings on popular guests */}
      {(isSelected || isComparison) && !isUserPoint && (
        <mesh ref={ringRef}>
          <ringGeometry args={[
            0.25 + size * 0.5,  // Inner: fixed base + scaled portion
            0.35 + size * 0.5,  // Outer: fixed base + scaled portion
            32
          ]} />
          <meshBasicMaterial
            color={isComparison ? "#FF8C42" : color}
            transparent
            opacity={isSelected ? 0.5 : 0.3}
            side={2} // DoubleSide
          />
        </mesh>
      )}
      
      {/* Gentle hover ring - fixed base + small size factor */}
      {isHovered && !isSelected && !isComparison && (
        <mesh ref={hoverRingRef}>
          <ringGeometry args={[0.18 + size * 0.4, 0.25 + size * 0.4, 32]} />
          <meshBasicMaterial
            color={color}
            transparent
            opacity={0.3}
            side={2} // DoubleSide
          />
        </mesh>
      )}
      
      <mesh
        ref={meshRef}
        onPointerOver={() => onHover(true)}
        onPointerOut={() => onHover(false)}
        onClick={(e) => {
          e.stopPropagation(); // Prevent event from bubbling to background
          onClick();
        }}
        scale={targetScale}
      >
        <sphereGeometry args={[size, 16, 16]} />
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={targetEmissiveIntensity}
          metalness={0.3}
          roughness={0.4}
          transparent
          opacity={targetOpacity}
        />
      </mesh>

      {/* Name label - shown when selected or hovered */}
      {showLabel && (
        <Html
          position={[0, size * 1.5 + 0.2, 0]}
          center
          distanceFactor={10}
          style={{ pointerEvents: 'none', userSelect: 'none', zIndex: 10 }}
          wrapperClass="guest-label-wrapper"
        >
          <div
            className="guest-label"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#2D1810',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
            }}
          >
            {name === '__USER__' ? 'Beautiful You' : String(name)}
          </div>
        </Html>
      )}
    </group>
  );
});
